import type { MapData } from '@etown/shared';
import type { IInputController } from '../game/IInputController';
import { TopDownInput } from '../game/TopDownInput';
import { istTouchGeraet } from '../lib/eingabeart';
import { useTouchSteuerungStore } from '../stores/touchSteuerungStore';
import type { ViewBundle, ViewMode } from '../game/view';
import { hasWebGL2 } from '../renderer3d/webglSupport';
import { useViewStore } from '../stores/viewStore';
import { WorldRenderer } from './WorldRenderer';
import { createTheme } from './theme/themeRegistry';

/**
 * Setzt eine Ansicht aus Renderer und passender Steuerung zusammen. Der
 * einzige Ort, an dem sich die Anwendung auf eine konkrete Technik festlegt —
 * `GameRuntime` kennt nur die Interfaces.
 *
 * three.js und die Ego-Steuerung werden erst beim Umschalten nachgeladen.
 * Wer bei der Draufsicht bleibt, laedt sie nie — im Firmennetz mit
 * Long-Polling zaehlt jedes Byte.
 */
export async function createView(mode: ViewMode, map: MapData, initialYaw = 0): Promise<ViewBundle> {
  if (mode === '3d') {
    const [{ WorldRenderer3D }, { createTheme3D }, input] = await Promise.all([
      import('../renderer3d/WorldRenderer3D'),
      import('../renderer3d/theme/theme3dRegistry'),
      egoSteuerung(initialYaw),
    ]);
    return {
      // Der zweite Name ist der des 2D-Themes: er waehlt die Figuren der
      // anderen Spieler. Sie bleiben in jedem 3D-Stil dieselben.
      //
      // Die Stilwahl kommt aus dem Store und wird bei jedem Aufbau frisch
      // gelesen — daran haengt das Umschalten im laufenden Betrieb: das
      // Popover merkt die neue Wahl an und laesst die Ansicht neu bauen.
      renderer: new WorldRenderer3D(createTheme3D(map.theme, useViewStore.getState().stil3d), map.theme),
      input,
    };
  }
  return {
    renderer: new WorldRenderer(createTheme(map.theme), 'play'),
    input: new TopDownInput(),
  };
}

/**
 * Steuerung der Ego-Ansicht: Finger oder Maus. Auf einem Touch-Geraet gibt
 * es keinen Zeigerfang, dort uebernimmt der virtuelle Joystick; seine Lage
 * meldet er an den Store, aus dem die Anzeige liest.
 */
async function egoSteuerung(initialYaw: number): Promise<IInputController> {
  if (istTouchGeraet()) {
    const { TouchFirstPersonInput } = await import('../game/TouchFirstPersonInput');
    return new TouchFirstPersonInput(initialYaw, useTouchSteuerungStore.getState().setStick);
  }
  const { FirstPersonInput } = await import('../game/FirstPersonInput');
  return new FirstPersonInput(initialYaw);
}

/** Ohne WebGL2 bleibt es still bei der Draufsicht. */
export function is3dAvailable(): boolean {
  return hasWebGL2();
}
